// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import {
  deleteChronicMedication,
  updateChronicMedication,
  deleteAcuteMedication,
  updateAcuteMedication,
} from '../../../actions/treatmentActions';

import DateText from '../../DateText.jsx';
import Table from '../../Table.jsx';
import Typeahead from '../../Typeahead.jsx';

type Props = {
  activePatientId: string,
  chronicMedications: Object[],
  acuteMedications: Object[],
  problemIdToProblem: Object,
  deleteChronicMedication: Function,
  updateChronicMedication: Function,
  deleteAcuteMedication: Function,
  updateAcuteMedication: Function,
  current: boolean,
  resolved: boolean,
  acute: boolean,
  chronic: boolean,
}

class MedicationTableComponent extends Component<Props> {
  /* eslint-disable no-param-reassign */
  transformDates(formValues) {
    formValues.startDate = moment(formValues.startDate, 'DD-MM-YYYY').toISOString();
    formValues.endDate = moment(formValues.endDate, 'DD-MM-YYYY').toISOString();
  }

  handleDeleteRow = (id: string, row: Object) => {
    if (this.props.chronic || row.chronic) {
      this.props.deleteChronicMedication(this.props.activePatientId, id);
    } else if (this.props.acute || row.acute) {
      this.props.deleteAcuteMedication(this.props.activePatientId, id);
    }
  }

  handleSaveRow = (id: string, formData: Object, row: Object) => {
    this.transformDates(formData.values);
    if (this.props.chronic || row.chronic) {
      this.props.updateChronicMedication(this.props.activePatientId,
        {
          treatmentId: id,
          ...formData.values,
        });
    } else if (this.props.acute || row.acute) {
      this.props.updateAcuteMedication(this.props.activePatientId,
        {
          treatmentId: id,
          ...formData.values,
        });
    }
  }

  render() {
    const { problemIdToProblem } = this.props;
    const columns = [
      {
        Header: 'Start Date',
        id: 'startDate',
        cell: item => (item.startDate ? moment(item.startDate).format() : '--'),
        editCell: item => <DateText field='startDate' item={item} />,
        width: 100,
      },
      {
        Header: 'End Date',
        id: 'endDate',
        cell: item => (item.endDate ? moment(item.endDate).format() : '(ongoing)'),
        editCell: item => <DateText field="endDate" item={item} nullable />,
        width: 100,
      },
      {
        Header: 'Name of Rx',
        id: 'drugName',
        editCell: item => <Typeahead field='drugName' initialValue={item.drugName} endpoint='rxName' />,
        width: 200,
      },
      {
        Header: 'Dose',
        id: 'dose',
        editCell: item => <Typeahead field='dose' initialValue={item.dose} endpoint='dose' />,
        width: 100,
      },
      {
        Header: 'Frequency',
        id: 'time',
        editCell: item => <Typeahead field='time' initialValue={item.time} endpoint='time' />,
        width: 100,
      },
      {
        Header: 'Route',
        id: 'route',
        editCell: item => <Typeahead field='route' initialValue={item.route} endpoint='route' />,
        width: 100,
      },
      {
        Header: 'Diagnosis',
        id: 'diagnosis',
        cell: (item) => {
          if (item.problemId in problemIdToProblem && problemIdToProblem[item.problemId].diagnosis) {
            return problemIdToProblem[item.problemId].diagnosis;
          }
          return '--';
        },
      },
      {
        Header: 'Diagnosis Date',
        id: 'diagnosisDate',
        cell: (item) => {
          if (item.problemId in problemIdToProblem && problemIdToProblem[item.problemId].onsetDate) {
            return moment(problemIdToProblem[item.problemId].onsetDate).format();
          }
          return '--';
        },
        width: 120,
      },
    ];

    let medications = [];
    let { acuteMedications, chronicMedications } = this.props;
    if (this.props.acute) {
      medications = acuteMedications;
    } else if (this.props.chronic) {
      medications = chronicMedications;
    } else {
      acuteMedications = acuteMedications.map(medication => ({ ...medication, acute: true }));
      chronicMedications = chronicMedications.map(medication => ({ ...medication, chronic: true }));
      medications = acuteMedications.concat(chronicMedications);
    }

    if (this.props.current) {
      medications = medications.filter(med => !med.endDate || moment(med.endDate) > moment());
    } else if (this.props.resolved) {
      medications = medications.filter(med => med.endDate && moment(med.endDate) <= moment());
    }

    medications = medications.sort((a, b) => {
      if (a.endDate && !b.endDate) {
        return 1;
      }
      if (b.endDate && !a.endDate) {
        return -1;
      }
      if (!a.startDate) {
        return 1;
      }
      if (!b.startDate) {
        return -1;
      }
      if (a.startDate < b.startDate) {
        return 1;
      }
      if (b.startDate < a.startDate) {
        return -1;
      }
      return 0;
    });

    return (
      <Table
        columns={columns}
        data={medications}
        handleDeleteRow={this.handleDeleteRow}
        handleSaveRow={this.handleSaveRow}
      />
    );
  }
}

const mapStateToProps = state => ({
  activePatientId: state.patient.data.activePatientId,
  chronicMedications: state.patient.data.chronicMedications,
  acuteMedications: state.patient.data.acuteMedications,
  problemIdToProblem: state.patient.data.problemIdToProblem,
});

const mapDispatchToProps = dispatch => ({
  deleteChronicMedication:
    (patientId, medicationId) => dispatch(deleteChronicMedication(patientId, medicationId)),
  updateChronicMedication:
    (patientId, medication) => dispatch(updateChronicMedication(patientId, medication)),
  deleteAcuteMedication:
    (patientId, medicationId) => dispatch(deleteAcuteMedication(patientId, medicationId)),
  updateAcuteMedication:
    (patientId, medication) => dispatch(updateAcuteMedication(patientId, medication)),
});

const MedicationTable = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MedicationTableComponent);

export default MedicationTable;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/treatment/MedicationTable.jsx